import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Lock, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import PinRecovery from "./PinRecovery";

export default function PinDialog({ open, onClose, onSuccess, title = "Parent PIN required", subtitle = "Enter your PIN to continue." }) {
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);
  const [shake, setShake] = useState(false);
  const [recovering, setRecovering] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/settings/verify-pin", { pin });
      if (data.ok) {
        setPin("");
        onSuccess();
      } else {
        setShake(true);
        setTimeout(() => setShake(false), 500);
        toast.error("Incorrect PIN");
      }
    } catch { toast.error("Could not verify PIN"); }
    finally { setLoading(false); }
  };

  const close = () => { setPin(""); setRecovering(false); onClose(); };

  return (
    <AnimatePresence>
      {open && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[130] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          data-testid="pin-dialog" onClick={close}>
          {!recovering ? (
          <motion.form onSubmit={submit} onClick={(e) => e.stopPropagation()}
            initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0, x: shake ? [0, -10, 10, -8, 8, 0] : 0 }} exit={{ scale: 0.9, opacity: 0 }}
            className="relative bg-white rounded-3xl shadow-2xl p-8 max-w-xs w-full text-center">
            <button type="button" onClick={close} className="absolute top-3 right-3 h-8 w-8 rounded-full flex items-center justify-center text-slate-400 hover:bg-slate-100"><X className="h-4 w-4" /></button>
            <span className="h-14 w-14 rounded-2xl bg-[#2EC4B6]/15 text-[#2EC4B6] flex items-center justify-center mx-auto"><Lock className="h-7 w-7" /></span>
            <h3 className="mt-4 text-xl font-display font-bold text-slate-900">{title}</h3>
            <p className="text-sm text-slate-500 mt-1">{subtitle}</p>
            <input data-testid="pin-input" inputMode="numeric" autoFocus value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="••••"
              className="mt-5 w-full text-center text-3xl tracking-[0.5em] font-bold rounded-2xl border-2 border-slate-200 h-16 focus:border-[#2EC4B6] outline-none" />
            <Button type="submit" data-testid="pin-submit-btn" disabled={loading || pin.length < 4}
              className="mt-5 w-full h-12 rounded-xl bg-[#2EC4B6] hover:bg-[#26a69a] font-bold">
              {loading ? "Checking…" : "Unlock"}
            </Button>
            <p className="text-xs text-slate-400 mt-3">Default PIN: 1234</p>
            <button type="button" onClick={() => setRecovering(true)} data-testid="forgot-pin-link"
              className="mt-2 text-sm font-semibold text-[#2EC4B6] hover:underline">Forgot PIN?</button>
          </motion.form>
          ) : (
            <motion.div onClick={(e) => e.stopPropagation()} initial={{ scale: 0.9 }} animate={{ scale: 1 }}
              className="relative bg-white rounded-3xl shadow-2xl p-8 max-w-xs w-full">
              <PinRecovery onBack={() => setRecovering(false)} onDone={() => setRecovering(false)} />
            </motion.div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
